import * as React from "react";
import { useState } from "react";
import PageContainer from "../core/PageContainer";
import Breadcrumbs from "../core/Breadcrumbs";
import { wrapComponentForErrors } from "../utility/ErrorHandler";
import usePromise from "../hooks/usePromise";
import {
  normalizeApiArrayResponse,
  normalizeArray,
} from "../../libs/api_utils";
import {
  getAllCoursesCategorySorted,
  setCoursesOrder,
} from "../../libs/courses";
import { DragDropContext } from "react-beautiful-dnd";
import ContainerDroppable from "../core/ContainerDroppable";
import ItemDraggable from "../core/ItemDraggable";
import { formatDndData, reorderItemInArray } from "../../libs/data_formatting";
import styled from "styled-components";
import Button from "../core/Button";
import Loader from "../core/Loader";
import { toast } from "../../utils";
import * as Sentry from "@sentry/browser";

interface IProps {
  userContext: IUserContextData;
}

const DragDropContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const CategoryContainer = styled.div`
  border: 1px solid lightgrey;
  margin: 5px;
  border-radius: 5px;
  width: 600px;
  background: white;
  @media (max-width: 1500px) {
    width: 500px;
  }
  @media (max-width: 1300px) {
    width: 400px;
  }
  @media (max-width: 1100px) {
    width: 100%;
  }
`;

const CourseContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  border: 1px solid lightgrey;
  background-color: white;
  padding: 10px;
  margin: 10px;
  border-radius: 5px;
`;

const SectionHeaderContainer = styled.div`
  padding: 10px;
  border-bottom: 1px solid #f2f2f2;
  margin-bottom: 5px;
  display: flex;
  justify-content: center;
`;

const CategoryHeaderContainer = styled.div`
  padding: 15px;
  background-color: #667782;
  margin-bottom: 5px;
  color: white;
  text-transform: capitalize;
`;

const ItemContainer = styled.div`
  width: 150px;
  margin-right: 20px;
  display: flex;
  align-items: center;
`;

const NameContainer = styled.div`
  width: 250px;
  margin-right: 25px;
  display: flex;
  align-items: center;
`;

const ButtonContainer = styled.div`
  padding: 5px 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid lightgrey;
  margin: 0 10px;
  border-radius: 5px;
  background-color: #f2f2f2;
`;

const CoursesIndexAdmin = ({ userContext: userContextData }: IProps) => {
  // Course categories
  const [categories, setCategories] = useState([]);
  // Course listing
  const [courses, { loading }] = usePromise(
    () =>
      getAllCoursesCategorySorted({ ranked: "category" }).then(resp => {
        // Format categories data (and setting state categories) before returning courses
        if (resp.included) {
          const normalizedCategories: { id: string }[] = normalizeArray(
            resp.included
          );
          const normalizedCourses: {
            id: string;
          }[] = normalizeApiArrayResponse(resp);
          // Format categories data by adding an attribute 'publishedCourses'
          // that contains an array of published course Ids that belongs to the category
          const categoriesPublishedCourses = formatDndData(
            normalizedCategories,
            normalizedCourses.filter(c => c["status"] === "published"),
            { newAttributeName: "publishedCourses" }
          );
          // Format categories data by adding an attribute 'unpublishedCourses'
          // that contains an array of UNpublished course Ids that belongs to the category
          const categoriesAllCourses = formatDndData(
            categoriesPublishedCourses,
            normalizedCourses.filter(c => c["status"] === "draft"),
            { newAttributeName: "unpublishedCourses" }
          );
          // Keep track of the original course order (in case user cancels the order)
          const finalCategories = categoriesAllCourses.map(category => ({
            ...category,
            originalCourses: [...category["publishedCourses"]],
          }));
          setCategories(finalCategories);
        }
        // Set courses with result
        return normalizeApiArrayResponse(resp);
      }),
    []
  );
  // Keep track of what categories were modified, for save button
  const [modifiedCategories, setModifiedCategories] = useState([]);
  const [updating, setUpdating] = useState(false);

  /**
   * Callback to handle what happens after the end of a drag
   * Currently performing optimistic update
   */
  const onDragEnd = ({ destination, source, draggableId }) => {
    // Exit if no destination
    if (!destination) {
      return;
    }
    // Not allowing drop between lists for now
    if (source.droppableId !== destination.droppableId) {
      return;
    }
    // Exit if dropped in the same place
    if (destination.index === source.index) {
      return;
    }
    // Get the category that was modified
    const category = categories.find(c => c.id === source.droppableId);
    // Reorder category.publishedCourses, this keeps track of the ordering within the category
    const reorderedList = reorderItemInArray(
      category.publishedCourses,
      source.index,
      destination.index
    );
    if (reorderedList === undefined) {
      // Something bad happened
      return;
    }
    // Create new category instead of mutating original
    const newCategory = { ...category, publishedCourses: reorderedList };
    // Create new list of categories
    const newCategoryList = categories.map(category =>
      newCategory.id === category.id ? newCategory : category
    );
    // Set state for categories list
    setCategories(newCategoryList);
    // Track whats been updated so we can display save button on correct list
    setModifiedCategories([...modifiedCategories, newCategory.id]);
  };

  /**
   * Save course order to backend
   * @param categoryId
   */
  const updateCourseOrder = async categoryId => {
    setUpdating(true);
    const category = categories.find(category => categoryId === category.id);
    try {
      const result = await setCoursesOrder(
        category.publishedCourses,
        category.id
      );
      setUpdating(false);
      if (result) {
        // Successfully persisted, copy this course order to original list
        const newCategories = categories.map(category => ({
          ...category,
          originalCourses: [...category.publishedCourses],
        }));
        setCategories(newCategories);
        // Remove from modified list
        const newModifiedCategories = modifiedCategories.filter(
          c => c !== categoryId
        );
        setModifiedCategories([...newModifiedCategories]);
        toast.success("Successfully reordered courses");
      }
    } catch (e) {
      setUpdating(false);
      toast.error("Reordering save failed");
      Sentry.captureException(e);
    }
  };

  const handleCancel = category => {
    // Copy original course order to published
    const newCategory = {
      ...category,
      publishedCourses: category.originalCourses,
    };
    const newCategories = categories.map(c =>
      c.id === newCategory.id ? newCategory : c
    );
    setCategories(newCategories);
    // Removed from modified listing
    const newModifiedCategories = modifiedCategories.filter(
      c => c !== newCategory.id
    );
    setModifiedCategories([...newModifiedCategories]);
  };

  if (loading) {
    return <Loader fullPageOverlay={true} size={"5em"} />;
  }

  return (
    <PageContainer>
      <Breadcrumbs crumbs={[{ label: "Courses" }]} />
      <a
        href={Routes.new_library_course_path()}
        style={{ marginLeft: "5px" }}
        className="btn btn-info"
      >
        <i style={{ marginRight: "4px" }} className="fa fa-plus" />
        Add Course
      </a>
      <DragDropContainer>
        <DragDropContext onDragEnd={onDragEnd}>
          {categories.map(category => {
            const categoryCourses = category.publishedCourses.map(courseId =>
              courses.find(course => course.id == courseId)
            );
            const categoryUnpublishedCourses = category.unpublishedCourses.map(
              courseId => courses.find(course => course.id == courseId)
            );
            return (
              <CategoryContainer key={category.id}>
                <ContainerDroppable category={category} items={categoryCourses}>
                  <SectionHeader category={category} />
                  {categoryCourses.map((course, index) => (
                    <ItemDraggable
                      key={course.id}
                      itemId={course.id}
                      index={index}
                    >
                      <CourseContent>
                        <CourseListing course={course} />
                      </CourseContent>
                    </ItemDraggable>
                  ))}
                </ContainerDroppable>
                {modifiedCategories.includes(category.id) && (
                  <ButtonContainer>
                    <span style={{ marginRight: "5px" }}>Save Order? </span>
                    <Button
                      loading={updating}
                      color={"info"}
                      size={"sm"}
                      onClick={() => updateCourseOrder(category.id)}
                    >
                      <i
                        style={{ margin: "2px 5px" }}
                        className="fa fa-check"
                      />
                    </Button>
                    &nbsp;
                    <Button
                      loading={updating}
                      color={"danger"}
                      size={"sm"}
                      onClick={() => handleCancel(category)}
                    >
                      <i
                        style={{ margin: "2px 5px" }}
                        className="fa fa-times"
                      />
                    </Button>
                  </ButtonContainer>
                )}
                {categoryUnpublishedCourses &&
                  categoryUnpublishedCourses.length > 0 && (
                    <SectionHeaderContainer> Drafts </SectionHeaderContainer>
                  )}
                {categoryUnpublishedCourses.map((course, index) => (
                  <CourseContent key={course.id}>
                    <CourseListing course={course} />
                  </CourseContent>
                ))}
              </CategoryContainer>
            );
          })}
        </DragDropContext>
        <DragDropContext />
      </DragDropContainer>
    </PageContainer>
  );
};

/**
 * Category header
 */
const SectionHeader = ({ category }) => {
  return (
    <CategoryHeaderContainer>
      <i className={"fa fa-folder " + category.color} /> {category.name}
    </CategoryHeaderContainer>
  );
};

/**
 * Generates each course row
 */
const CourseListing = ({ course }) => {
  return (
    <>
      <NameContainer>
        {course.status === "published" ? (
          <>
            <i style={{ margin: "2px 5px" }} className="fa fa-sort" />
            <a href={course.links.editUrl}>
              <span>{course.title}</span>
            </a>
          </>
        ) : (
          <span style={{ marginLeft: "18px" }}>
            <a href={course.links.editUrl}>
              {course.title}
              <span
                style={{ backgroundColor: "#ffa323", margin: "0 5px" }}
                className="badge"
              >
                Draft
              </span>
            </a>
          </span>
        )}
      </NameContainer>
      <ItemContainer>
        <span style={{ backgroundColor: "#afd1db" }} className="badge">
          {course.lessonsCount + " Lessons"}
        </span>
      </ItemContainer>
      <ItemContainer>
        <a className="btn btn-primary btn-sm" href={course.links.reportUrl}>
          <i style={{ margin: "2px 5px" }} className="fa fa-bar-chart" />
          View Reports
        </a>
      </ItemContainer>
    </>
  );
};

export default wrapComponentForErrors(CoursesIndexAdmin);



// WEBPACK FOOTER //
// ./app/javascript/components/pages/CoursesIndexAdmin.tsx